import React, { useEffect, useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { getAccessories, deleteAccessory } from '../../actions/accessory';
import { Plus, Search, Edit, Trash2, Package, Settings, AlertTriangle } from 'lucide-react';

const Accessories = ({ getAccessories, deleteAccessory, accessories, loading }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [accessoryToDelete, setAccessoryToDelete] = useState(null);
    const [filteredAccessories, setFilteredAccessories] = useState([]);

    useEffect(() => {
        getAccessories();
    }, []);

    useEffect(() => {
        if (accessories) {
            const filtered = accessories.filter(accessory =>
                accessory.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                accessory.brand?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                accessory.productType?.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredAccessories(filtered);
        }
    }, [accessories, searchTerm]);

    const handleDeleteClick = (accessory) => {
        setAccessoryToDelete(accessory);
        setShowDeleteModal(true);
    };

    const confirmDelete = () => {
        if (accessoryToDelete) {
            deleteAccessory(accessoryToDelete._id);
            setShowDeleteModal(false);
            setAccessoryToDelete(null);
        }
    };

    const cancelDelete = () => {
        setShowDeleteModal(false);
        setAccessoryToDelete(null);
    };

    const getProductTypeColor = (type) => {
        switch (type?.toLowerCase()) {
            case 'electronics': return 'primary';
            case 'clothing': return 'success';
            case 'sports': return 'warning';
            case 'home': return 'info';
            default: return 'secondary';
        }
    };

    return (
        <Fragment>
            <div className="container-fluid py-4">
                {/* Header Section */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center flex-wrap">
                            <div>
                                <h1 className="h3 mb-2 text-gray-800 fw-bold">
                                    <Package size={32} className="me-3 text-primary" />
                                    Accessories
                                </h1>
                                <p className="text-muted mb-0">
                                    Manage your product accessories and add-ons
                                </p>
                            </div>
                            <Link 
                                to="/inventory/v1.2/accessories/new"
                                className="btn btn-primary btn-lg shadow-sm d-flex align-items-center"
                            >
                                <Plus size={20} className="me-2" />
                                Add New Accessory
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Search and Stats Section */}
                <div className="row mb-4">
                    <div className="col-lg-6">
                        <div className="card shadow-sm">
                            <div className="card-body">
                                <div className="input-group">
                                    <span className="input-group-text bg-light border-end-0">
                                        <Search size={18} className="text-muted" />
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control border-start-0"
                                        placeholder="Search accessories by name, brand, or type..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-6">
                        <div className="card shadow-sm bg-primary text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <div className="h4 mb-0">{accessories?.length || 0}</div>
                                        <div className="small">Total Accessories</div>
                                    </div>
                                    <Settings size={32} className="opacity-75" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Accessories Grid */}
                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3 text-muted">Loading accessories...</p>
                    </div>
                ) : filteredAccessories && filteredAccessories.length > 0 ? (
                    <div className="row">
                        {filteredAccessories.map((accessory) => (
                            <div key={accessory._id} className="col-xl-4 col-lg-6 col-md-6 mb-4">
                                <div 
                                    className="card shadow-sm border-0 h-100" 
                                    style={{
                                        borderRadius: '0.75rem',
                                        transition: 'all 0.3s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                        e.currentTarget.style.boxShadow = '0 0.5rem 1rem rgba(0, 0, 0, 0.15)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = '';
                                    }}
                                >
                                    <div className="card-header bg-white border-bottom-0 pb-0">
                                        <div className="d-flex justify-content-between align-items-start">
                                            <div className="flex-grow-1">
                                                <h5 className="card-title mb-1 text-primary fw-bold">
                                                    {accessory.name}
                                                </h5>
                                                <span 
                                                    className={`badge bg-${getProductTypeColor(accessory.productType)} mb-2`}
                                                    style={{ fontSize: '0.75rem' }}
                                                >
                                                    {accessory.productType || 'General'}
                                                </span>
                                            </div>
                                            <div className="btn-group">
                                                <Link 
                                                    to={`/inventory/v1.2/accessories/${accessory._id}`}
                                                    className="btn btn-sm btn-outline-primary"
                                                    title="Edit Accessory"
                                                >
                                                    <Edit size={14} />
                                                </Link>
                                                <button 
                                                    className="btn btn-sm btn-outline-danger"
                                                    onClick={() => handleDeleteClick(accessory)}
                                                    title="Delete Accessory"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="card-body pt-2">
                                        <div className="row g-3">
                                            <div className="col-6">
                                                <div className="text-center">
                                                    <div className="h6 mb-0 text-info fw-bold">{accessory.brand || 'N/A'}</div>
                                                    <div className="small text-muted">Brand</div>
                                                </div>
                                            </div>
                                            <div className="col-6">
                                                <div className="text-center">
                                                    <div className="h6 mb-0 text-success">{accessory.images?.length || 0}</div>
                                                    <div className="small text-muted">Images</div>
                                                </div>
                                            </div>
                                        </div>
                                        {accessory.description && (
                                            <div className="mt-3">
                                                <p className="text-muted small mb-0" style={{ 
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden'
                                                }}>
                                                    {accessory.description}
                                                </p>
                                            </div>
                                        )}
                                        <div className="mt-3 pt-3 border-top">
                                            <small className="text-muted">
                                                Created: {new Date(accessory.createdAt).toLocaleDateString()}
                                            </small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-5">
                        <Package size={64} className="text-muted mb-3" />
                        <h4 className="text-muted mb-3">No Accessories Found</h4>
                        <p className="text-muted mb-4">
                            {searchTerm ? 'No accessories match your search criteria.' : 'Get started by adding your first accessory.'}
                        </p>
                        {!searchTerm && (
                            <Link 
                                to="/inventory/v1.2/accessories/new"
                                className="btn btn-primary btn-lg"
                            >
                                <Plus size={20} className="me-2" />
                                Add Your First Accessory
                            </Link>
                        )}
                    </div>
                )}
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div 
                    className="modal fade show" 
                    style={{ 
                        display: 'block',
                        zIndex: 1050, // Ensure modal is above backdrop
                        position: 'fixed', // Ensure modal is fixed
                        left: 0,
                        top: 0,
                        width: '100vw',
                        height: '100vh',
                        overflow: 'auto',
                        background: 'rgba(0,0,0,0.5)' // Semi-transparent overlay
                    }} 
                    tabIndex="-1"
                >
                    <div className="modal-dialog modal-dialog-centered" style={{ pointerEvents: 'auto' }}>
                        <div className="modal-content">
                            <div className="modal-header border-0">
                                <h5 className="modal-title text-danger">
                                    <AlertTriangle size={20} className="me-2" />
                                    Confirm Delete
                                </h5>
                                <button type="button" className="btn-close" onClick={cancelDelete}></button>
                            </div>
                            <div className="modal-body">
                                <p>Are you sure you want to delete <strong>"{accessoryToDelete?.name}"</strong>?</p>
                                <p className="text-muted small mb-0">This action cannot be undone.</p>
                            </div>
                            <div className="modal-footer border-0">
                                <button type="button" className="btn btn-secondary" onClick={cancelDelete}>
                                    Cancel
                                </button>
                                <button 
                                    type="button" 
                                    className="btn btn-danger" 
                                    onClick={confirmDelete}
                                    style={{ position: 'relative', zIndex: 1055 }} // Ensure button is clickable
                                >
                                    <Trash2 size={16} className="me-2" />
                                    Delete Accessory
                                </button>
                            </div>
                        </div>
                    </div>
                    {/* Removed the separate backdrop div that was causing issues */}
                </div>
            )}
        </Fragment>
    );
};

Accessories.propTypes = {
    getAccessories: PropTypes.func.isRequired,
    deleteAccessory: PropTypes.func.isRequired,
    accessories: PropTypes.array,
    loading: PropTypes.bool.isRequired
};

const mapStateToProps = (state) => ({
    accessories: state.accessory.accessories,
    loading: state.accessory.loading
});

export default connect(mapStateToProps, { getAccessories, deleteAccessory })(Accessories); 